import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MsalAuthService } from '@app/services/msal-auth/msal-auth.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject, Subscription, filter, takeUntil } from 'rxjs';

@Component({
    selector: 'app-msal-provider-wrapper',
    templateUrl: './msal-provider-wrapper.component.html',
    styleUrls: ['./msal-provider-wrapper.component.scss'],
})
export class MsalProviderWrapperComponent implements OnInit, OnDestroy {
    private readonly _destroyingSubject$ = new Subject<void>();
    private authService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private msalAuthService = inject(MsalAuthService);
    private navigationService = inject(NavigationService);
    private subs$ = new Subscription();

    ngOnInit() {
        // eslint-disable-next-line rxjs/no-ignored-subscribe, rxjs/no-ignored-subscription
        this.authService.handleRedirectObservable().subscribe();
        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
        void this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.ACCOUNT_ADDED ||
                        msg.eventType === EventType.ACCOUNT_REMOVED,
                ),
            )
            .subscribe((result: EventMessage) => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    // eslint-disable-next-line no-console
                    console.log(result);
                    this.navigationService.goToDefaultPage(
                        this.msalAuthService.getAppClaims(),
                    );
                } else {
                    this.msalAuthService.setLoginDisplay();
                }
            });

        void this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroyingSubject$),
            )
            .subscribe(() => {
                this.msalAuthService.setLoginDisplay();
                this.checkAndSetActiveAccount();
            });
    }

    ngOnDestroy(): void {
        this.subs$.unsubscribe();
        this._destroyingSubject$.next(undefined);
        this._destroyingSubject$.complete();
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        const activeAccount = this.authService.instance.getActiveAccount();
        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            const accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
        }
    }
}
